import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import { PagedCrudService } from "@features/grid";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { UserHelperService } from "@main/users/user-helper.service";
import { IEpisode } from "@project/models/episodes/episode";
import { Note } from "@project/models/notes/note";
import { NoteState } from "@project/models/notes/note-state";
import { NoteField } from "../../models/notes/note-field";
import { EntityId } from "@bbwt/interfaces";
import { EpisodeService } from "@project/episode/episode.service";
import { NotificationService } from "@main/notification/notification.service";
import { NotificationType } from "@main/notification/notification-type";
import { UserService } from "@main/users";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { NoteTreatment } from "@project/models/notes/note-treatment";
import { NoteOfflineStorage } from "./note-offline.storage";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { EpisodeOfflineStorage } from "@project/episode/episode-offline.storage";
import { WalkInOfflineStorage } from "@project/walk-ins/classes/walk-in-offline.storage";
import { WalkIn } from "@project/walk-ins/classes/walk-in";
import * as moment from "moment/moment";
import { SetUTCTimeZone } from "@bbwt/utils";
import { AutoSaveHeader } from "@mipro/services/auto-save.interceptor";
import { HistoricalItem } from "@mipro/classes/historical-item";

@Injectable({
    providedIn: "root"
})
export class NoteService extends PagedCrudService<Note> {
    public readonly url = "api/note";
    public readonly entityTitle = "Note";

    public readonly soapFields = ["subjective", "objective", "examinationAndAssessment", "plan", "clinicalNote", "treatmentsNotes"];

    public readonly defaultQuickNote = { name: "clinicalNote", label: "Quick Note", value: "", selected: true, display: true };

    public readonly defaultNoteFields = [
        { name: "presentingComplaint", label: "Complaint", value: "", selected: true, display: true },
        { name: "history", label: "History", value: "", selected: true, display: true },
        { name: "subjective", label: "Subjective", value: "", selected: true, display: true },
        { name: "objective", label: "Objective", value: "", selected: true, display: true },
        { name: "examinationAndAssessment", label: "Assessment", value: "", selected: true, display: true },
        { name: "impression", label: "Impression", value: "", selected: true, display: true },
        { name: "plan", label: "Plan", value: "", selected: true, display: true },
        { name: "comments", label: "Comments", value: "", selected: true, display: true },
        { name: "ravenNotes", label: "Raven Notes", value: "", selected: true, display: true },
        { name: "medication", label: "Medication", value: "", selected: true, display: false },
        { name: "followUp", label: "Follow Up", value: "", selected: true, display: false },
        { name: "procedure", label: "Procedure", value: "", selected: true, display: false },
        { name: "testRequest", label: "TestRequest", value: "", selected: true, display: false },
        { name: "referral", label: "Referral", value: "", selected: true, display: false },
        { name: "document", label: "Document", value: "", selected: true, display: false },
        { name: "allergy", label: "Allergy", value: "", selected: true, display: false },
        { name: "additional", label: "Additional", value: "", selected: true, display: false },
        { name: "examination", label: "Examination", value: "", selected: true, display: false },
        { name: "familyHistory", label: "Family History", value: "", selected: true, display: false },
        { name: "social", label: "Social", value: "", selected: true, display: false },
        { name: "result", label: "Result", value: "", selected: true, display: false },
        { name: "assessment", label: "Assessment", value: "", selected: true, display: false },

        this.defaultQuickNote
    ];

    defaultTreatmentNoteField = { name: "treatmentsNotes", label: "Treatment Notes", value: "", selected: true, display: true };

    public readonly episodeSender: IEpisode[] = [];

    public episodeService: EpisodeService;

    constructor(
        http: HttpClient,
        handlersFactory: HttpResponsesHandlersFactory,
        private userHelperService: UserHelperService,
        private notificationService: NotificationService,
        private userService: UserService,
        private patientService: PatientService,
        private appOnlineStateService: AppOnlineStateService
    ) {
        super(http, handlersFactory);
    }

    async create(item: Note, responseHandlerSettings?: IHttpResponseHandlerSettings, isAutoSave = false): Promise<Note> {
        const localEpisode: IEpisode = EpisodeOfflineStorage.get(item.episodeId);
        const offlineWalkIn = WalkInOfflineStorage?.get(item.walkInId);

        if (!localEpisode && !offlineWalkIn && this.appOnlineStateService.isAppOnline) {
            const headers = this.getAutoSaveHeaders(isAutoSave);
            return this.handleRequest(
                this.http.post<Note>(`${this.url}`, item, { headers }),
                this.handlersFactory.getForCreate(this.entityTitle, responseHandlerSettings)
            );
        } else {
            item.state = NoteState.StoredLocal;
            item.id = null;
            item.noteTreatments = item.selectedTreatments.map(t => <NoteTreatment>{ treatmentId: t.id, treatmentName: t.name });
            item.createdByUserId = this.userHelperService.userId;
            item.createdByUserFullName = this.userService.currentUser.fullName;
            item.createdByUserTitle = this.userService.currentUser.title;
            item.createdByUserRole = this.userService.currentUserRoles[0]?.name;
            item.lastUpdatedDate = new Date();

            let redirectParams = [
                {
                    name: "patientId",
                    value: this.patientService.currentPatientId.toString()
                }
            ];
            let redirectTo = "";
            if (item.prescriptionId == null && item.episodeId) {
                redirectParams.push({ name: "episodeId", value: item.episodeId.toString() });
                redirectTo = "/app/consultation-history";
            } else {
                redirectTo = "/app/prescriptions/new";
            }

            const notification = await this.notificationService.createOffline({
                id: null,
                title: "Note - DRAFT OFFLINE for " + this.patientService.currentPatientPlaceHolder.fullName + " pending upload",
                body: null,
                read: false,
                archived: false,
                created: new Date(),
                type: NotificationType.LocalNote,
                redirectTo: redirectTo,
                redirectParams: redirectParams,
                savedOffline: true
            });
            item.notificationId = notification.id;
            const note: Note = NoteOfflineStorage.save(item);
            return note;
        }
    }

    async get(id: EntityId, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<Note> {
        const note: Note = NoteOfflineStorage.get(id as string);
        return note ?? await super.get(id, responseHandlerSettings);
    }

    async update(id: EntityId, item: Note, responseHandlerSettings?: IHttpResponseHandlerSettings, isAutoSave = false): Promise<Note> {
        const note: Note = NoteOfflineStorage.get(id as string);

        if (note == null) {
            const headers = this.getAutoSaveHeaders(isAutoSave);
            return this.handleRequest(
                this.http.put<Note>(`${this.url}/${id}`, item, { headers }),
                this.handlersFactory.getForUpdate(this.entityTitle, responseHandlerSettings)
            );
        }

        return this.editLocalNote(item);
    }

    private editLocalNote(note: Note): Note {
        const previousNote: Note = NoteOfflineStorage.get(note.id as string);
        return NoteOfflineStorage.update({
            ...note,
            createdByUserId: previousNote.createdByUserId,
            createdByOrganization: previousNote.createdByOrganization,
            createdByUserFullName: previousNote.createdByUserFullName,
            createdByUserTitle: previousNote.createdByUserTitle,
            createdByUserRole: previousNote.createdByUserRole,
            notificationId: previousNote.notificationId,
            noteTreatments: note.selectedTreatments.map(
                t =>
                    <NoteTreatment>{
                        treatmentId: t.id,
                        treatmentName: t.name
                    }
            ),
            lastUpdatedDate: new Date()
        });
    }

    async saveLocalNoteOnline(id: string, episodeId?: string): Promise<Note> {
        if (this.appOnlineStateService.isAppOnline) {
            const note: Note = NoteOfflineStorage.remove(id);
            await this.notificationService.deleteOfflineNotification(note.notificationId);
            return await this.create({
                ...note,
                state: NoteState.Draft,
                id: null,
                noteTreatments: null,
                notificationId: null,
                createdByUserId: null,
                createdByUserFullName: null,
                createdByUserTitle: null,
                createdByUserRole: null,
                episodeId: episodeId ?? note.episodeId
            });
        }
    }

    // To avoid circular import between NoteService and EpisodeService
    setEpisodeService(episodeService: EpisodeService) {
        if (this.episodeService == null) {
            this.episodeService = episodeService;
        }
    }

    addEpisodeToSend(episode: IEpisode) {
        this.episodeSender.push(episode);
    }

    getEpisodeFromSender(episodeId) {
        const index: number = this.episodeSender.findIndex(e => e.id == episodeId);
        if (index == -1) {
            return null;
        }
        const episode: IEpisode = this.episodeSender.splice(index, 1)[0];
        return episode;
    }

    updateLocalNotesEpisodeId(oldEpisodeId: string, newEpisodeId: string): void {
        const notes = NoteOfflineStorage.getEpisodeNotes(oldEpisodeId);
        notes.forEach(n => this.editLocalNote({ ...n, episodeId: newEpisodeId }));
    }

    updateLocalNotesPrescriptionId(oldPrescriptionId: string, newPrescriptionId: string): void {
        const notes: Note[] = NoteOfflineStorage.getPrescriptionNotes(oldPrescriptionId);
        notes.forEach(n => this.editLocalNote({ ...n, prescriptionId: newPrescriptionId }));
    }

    public archive(id: string): Promise<void> {
        const note: Note = NoteOfflineStorage.get(id as string);

        if (note == null) {
            return this.handleRequest<void>(this.http.post<void>(`${this.url}/archive/${id}`, null));
        } else {
            this.deleteLocalNote(id);
        }
    }

    deleteLocalNote(id: string): Note {
        const note: Note = NoteOfflineStorage.remove(id);
        if (!note) {
            return null;
        }

        if (note.notificationId) {
            this.notificationService.deleteOfflineNotification(note.notificationId.toString());
        }
        note.notificationId = null;

        return note;
    }

    removeOfflineEpisodeNotes(episodeId: string): void {
        const notes = NoteOfflineStorage.getEpisodeNotes(episodeId) ?? [];
        notes.forEach(note => this.deleteLocalNote(note.id));
    }

    removeOfflinePrescriptionNotes(prescriptionId: string): void {
        const notes = NoteOfflineStorage.getPrescriptionNotes(prescriptionId) ?? [];
        notes.forEach(note => this.deleteLocalNote(note.id));
    }

    public sortNoteFields(noteFields: NoteField[]): NoteField[] {
        return noteFields.sort(
            (a, b) => this.defaultNoteFields.findIndex(x => x.name === a.name) - this.defaultNoteFields.findIndex(x => x.name === b.name)
        );
    }

    public addLables(noteFields: NoteField[]): void {
        const defaultFields = this.defaultNoteFields.concat([this.defaultTreatmentNoteField]);

        noteFields?.forEach(x => {
            x.label = defaultFields.find(defField => defField.name === x.name)?.label ?? x.label;
        });
    }

    public formatFieldValue(note: Note, field: NoteField): string {
        if (note.state == NoteState.ClinicalSafetyOfficerNote && field.name == this.defaultQuickNote.name) {
            const lines = field.value.split("\n");

            const TIME_OF_ARRIVAL_REGEXP_TEXT_INDEX = 1;
            const TIME_OF_ARRIVAL_REGEXP_DATE_INDEX = 2;
            const timeOfArrivalRegexp = RegExp("(Time of Arrival: )(.*)");

            let resultString = "";

            lines.forEach(line => {
                if (timeOfArrivalRegexp.test(line)) {
                    let match = timeOfArrivalRegexp.exec(line);
                    let date = match[TIME_OF_ARRIVAL_REGEXP_DATE_INDEX];
                    date = moment(SetUTCTimeZone(moment(date, "DD.MM.YYYY HH:mm").toDate())).format("DD.MM.YYYY HH:mm");
                    resultString += `${match[TIME_OF_ARRIVAL_REGEXP_TEXT_INDEX]}${date}\n`;
                    return;
                }

                resultString += line + "\n";
            });

            return resultString;
        }

        return field.value;
    }

    public getEditDeleteInfo(note: Note, episode: IEpisode): { canEdit: boolean; canDelete: boolean } {
        if (note.state == NoteState.StoredLocal && !this.appOnlineStateService.isAppOnline) {
            return { canDelete: true, canEdit: true };
        }

        // Walk-in notes may exist without an episode
        if (!note || (!episode && !note.walkInId)) {
            return { canDelete: false, canEdit: false };
        }

        const hasWrite = this.userHelperService.hasPermission(Permissions.WriteDiary);
        const ownerOrClinical =
            (this.userHelperService.hasRole(CoreRole.ClinicalAdmin) &&
                this.userHelperService.hasOrganization(note.createdOrganizationId)) ||
            this.userHelperService.userId == note.createdByUserId;
        const quickNoteValue = note.noteFields[0]?.value ?? "";
        const isWalkINQuickNote =
            !!note.walkInQuickNote ||
            (note.state == NoteState.WalkInDraft &&
                note.noteFields.length == 1 &&
                note.noteFields[0].name == this.defaultQuickNote.name &&
                (quickNoteValue.includes("Routine Treatment performed") ||
                    quickNoteValue.includes("Routine treatment performed") ||
                    quickNoteValue.includes("Walk-in created") ||
                    quickNoteValue.includes("Self-Directed Gym/Recovery")));

        const canEditEpisode = episode
            ? this.episodeService.canEditEpisode(episode) || this.userHelperService.hasRole(CoreRole.ClinicalAdmin)
            : !!note.walkInId;
        const canEdit =
            hasWrite &&
            ownerOrClinical &&
            !isWalkINQuickNote &&
            canEditEpisode &&
            !note.isArchived &&
            note.state !== NoteState.Active &&
            note.state !== NoteState.ClinicalSafetyOfficerNote;

        const canDelete =
            hasWrite && !isWalkINQuickNote && canEditEpisode && !note.isArchived && note.state !== NoteState.ClinicalSafetyOfficerNote;

        return { canEdit, canDelete };
    }

    deleteFormsData(formDataIds: string[]) {
        if (!formDataIds?.length) {
            return;
        }

        let params = new HttpParams();
        formDataIds.forEach((id, i) => (params = params.append("formDataId", id)));

        return this.handleRequest(this.http.delete<void>(`${this.url}/form-data`, { params }));
    }

    async isRavenSituationUpdate(id: string): Promise<boolean> {
        return this.appOnlineStateService.isAppOnline
            ? this.handleRequest<boolean>(
                  this.http.get<boolean>(`${this.url}/${id}/is-situation-update`),
                  this.handlersFactory.getDefault()
              )
            : false;
    }

    getHistoricalItems(noteId: string): Promise<HistoricalItem[]> {
        return this.handleRequest<HistoricalItem[]>(
            this.http.get<HistoricalItem[]>(`${this.url}/get-historical-items/${noteId}`),
            this.handlersFactory.getForReadAll());
    }

    private getAutoSaveHeaders(isAutoSave: boolean) {
        if (!isAutoSave) return;

        return new HttpHeaders({ [AutoSaveHeader]: "true" });
    }
}
